/**
 * Date and time *labels* — the read-only counterpart to `zoned-input`, which
 * deals in form values.
 *
 * A label may be rendered twice — once into the server's HTML and again during
 * hydration — so everything it reads has to be pinned or the two passes
 * disagree and React throws a text hydration mismatch (#418), discarding the
 * tree it was handed.
 *
 * Two inputs were not pinned. Calling `toLocaleDateString` with an omitted (or
 * explicitly undefined) locale resolves it — and the timezone with it —
 * against the *process* locale and zone, which on Vercel is en-US in UTC and in
 * the browser is whatever the viewer has: the same post rendered as "Sep 7" on
 * the server and "7 Sept" on an en-IN phone, and anything logged late in the
 * UTC day landed on a different calendar day in IST. That is deterministic —
 * every card carrying a post past the relative window mismatched. `Date.now()`
 * is the other: a post can cross a boundary ("now" → "1m") in the milliseconds
 * between the server render and hydration.
 *
 * The locale and zone are pinned here. The clock cannot be, so surfaces that
 * render a *relative* label mark the element `suppressHydrationWarning` — a
 * label one tick stale for a moment is the intended trade, and the whole tree
 * is not.
 *
 * ## Which formatter
 *
 * For an absolute label the choice turns on what the `Date` actually
 * represents, and getting it wrong is an off-by-one-*day* bug rather than a
 * cosmetic one:
 *
 * - **An instant** — a `createdAt`, a booking's `startTime`, anything that came
 *   out of the database or an ISO string. Use `formatAbsoluteDay`,
 *   `formatAbsoluteDateTime`, `formatAbsoluteTime`, or `formatAbsolute` for a
 *   custom shape. These read the instant in `APP_DEFAULT_TIME_ZONE`, so every
 *   viewer sees the one wall clock the business means.
 *
 * - **A calendar date** — a `Date` built from local parts (`new Date(y, m, 1)`,
 *   `new Date("2026-09-05T00:00:00")`) and read back with `getDate()` /
 *   `getDay()`, as every venue calendar grid does. Use `formatCalendarDay`.
 *   These are *not* instants: the same Y/M/D deliberately means the same square
 *   of the grid in every runtime. Formatting one in a named zone would shift the
 *   label a day for viewers east of that zone and desynchronise it from the
 *   `getDay()` weekday rendered right beside it.
 *
 * Where a label genuinely must track the *viewer's* zone — "your local time"
 * next to a fixture in another country — none of these apply; render
 * `<LocalDateTime>` (src/components/ui/local-date-time.tsx), which reserves the
 * line until mount instead of guessing on the server.
 *
 * ## Calling convention
 *
 * Every formatter here takes the value first and an **options object** second —
 * never a bare second argument. There are two kinds, and which one a formatter
 * takes follows from what it depends on:
 *
 * - `ClockOptions` (`{ now }`) for a label whose text depends on the current
 *   time: the two relative ladders, and `formatAbsoluteDay`, which drops the
 *   year when it is the current one.
 * - `Intl.DateTimeFormatOptions` for a label whose text depends only on a
 *   shape: `formatAbsolute`, `formatAbsoluteDateTime`, `formatAbsoluteTime`,
 *   `formatCalendarDay`.
 */

import { APP_DEFAULT_TIME_ZONE } from "./zoned-input";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

const RELATIVE_TIME_LOCALE = "en-IN";

/**
 * en-IN abbreviated month names, written out rather than asked of `Intl`.
 *
 * Pinning the locale was thought to be enough, and it is not. The month *names*
 * come from CLDR data compiled into whichever ICU the runtime ships, and that
 * data is not identical across runtimes: for `en-IN`, September is `Sept` in
 * Node 78's ICU and `Sep` in the browser's. Both are correct readings of `en-IN`
 * in their own ICU, which is exactly why the discrepancy is invisible until it
 * crashes — the server writes "29 Sept 2026", the client expects "29 Sep 2026",
 * and React throws a text hydration mismatch and throws the whole tree away.
 *
 * The names below are en-IN's, so the label the product intends survives; they
 * are just no longer at the mercy of whichever ICU is installed. Digits are
 * ASCII for the same reason — `en-IN` is Latin-digit by default, but a
 * non-Latin default would reintroduce the same class of split.
 */
const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * Numeric date parts in the app's zone.
 *
 * Deliberately numeric-only: `formatToParts` is then reading digits out of ICU
 * data, which is stable, rather than month names, which is not (see
 * `SHORT_MONTHS`). `en-US` because its numeric pattern is the plain
 * `MM/DD/YYYY` one with no ordering surprises.
 */
const ZONED_DATE_PARTS = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: APP_DEFAULT_TIME_ZONE,
});

type ZonedDateParts = { day: number; monthIndex: number; year: number };

function zonedDateParts(date: Date): ZonedDateParts | null {
  const parts = new Map<string, string>();
  for (const part of ZONED_DATE_PARTS.formatToParts(date)) {
    if (part.type === "day" || part.type === "month" || part.type === "year") {
      parts.set(part.type, part.value);
    }
  }

  const day = Number(parts.get("day"));
  const month = Number(parts.get("month"));
  const year = Number(parts.get("year"));
  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year)
  ) {
    return null;
  }
  return { day, monthIndex: month - 1, year };
}

function shortMonth(monthIndex: number): string {
  return SHORT_MONTHS[monthIndex] ?? "";
}

/**
 * A calendar day in the app's zone, as `"29 Sept 2026"` or `"29 Sept"`.
 *
 * The ICU-free replacement for a `month: "short"` `Intl.DateTimeFormat`, for
 * any label that is server-rendered and then hydrated. Both halves of the
 * hydration problem are closed here: the zone is pinned to
 * `APP_DEFAULT_TIME_ZONE` so the calendar day is the same in every runtime, and
 * the month name comes from a table rather than from the host's CLDR.
 */
function formatZonedShortDay(
  value: DateLike,
  { withYear }: { withYear: boolean },
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = zonedDateParts(date);
  if (!parts) return "";

  const month = shortMonth(parts.monthIndex);
  return withYear
    ? `${parts.day} ${month} ${parts.year}`
    : `${parts.day} ${month}`;
}

/** `"29 Sept"` — the day and month, no year, in the app's zone. */
export function formatDayMonth(
  value: DateLike,
  { weekday = false }: { weekday?: boolean } = {},
): string {
  const label = formatZonedShortDay(value, { withYear: false });
  if (!weekday || !label) return label;
  const parts = zonedDateParts(toDate(value));
  if (!parts) return "";
  const day = new Date(Date.UTC(parts.year, parts.monthIndex, parts.day));
  return `${SHORT_WEEKDAYS[day.getUTCDay()]} ${label}`;
}

/** `"29 Sept 2026"` — the day, month and year, in the app's zone. */
export function formatDayMonthYear(value: DateLike): string {
  return formatZonedShortDay(value, { withYear: true });
}

/**
 * Numeric date *and clock* parts in the app's zone — the same digits-only
 * reasoning as `ZONED_DATE_PARTS`. `hourCycle: "h23"` because `hour12: false`
 * renders midnight as `24` on some engines.
 */
const ZONED_DATE_TIME_PARTS = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: APP_DEFAULT_TIME_ZONE,
});

const SHORT_WEEKDAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

type ZonedDateTimeParts = ZonedDateParts & { hour: number; minute: number };

function zonedDateTimeParts(date: Date): ZonedDateTimeParts | null {
  const parts = new Map<string, string>();
  for (const part of ZONED_DATE_TIME_PARTS.formatToParts(date)) {
    parts.set(part.type, part.value);
  }

  const values = {
    day: Number(parts.get("day")),
    monthIndex: Number(parts.get("month")) - 1,
    year: Number(parts.get("year")),
    hour: Number(parts.get("hour")),
    minute: Number(parts.get("minute")),
  };
  return Object.values(values).every(Number.isFinite) ? values : null;
}

/** `"12:00 pm"` from zoned parts — the same shape as `formatTimeInput12Hour`. */
function clockLabel({ hour, minute }: { hour: number; minute: number }) {
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = hour < 12 ? "am" : "pm";
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

/**
 * The wall clock an instant shows in the app's zone, as `"12:00 pm"`.
 *
 * The ICU-free counterpart to `formatAbsoluteTime`, for labels that are
 * server-rendered and then hydrated. The day period is CLDR data too: for
 * `en-IN`, Node's ICU writes `PM` where the browser's writes `pm`.
 */
export function formatClockTime(value: DateLike): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = zonedDateTimeParts(date);
  return parts ? clockLabel(parts) : "";
}

/**
 * An instant as `"Sun, 11 Oct, 12:00 pm"` (or without the weekday) in the
 * app's zone.
 *
 * The ICU-free counterpart to `formatAbsoluteDateTime` with a short month.
 * Beyond the month name and day period, the *glue* between date and time is
 * CLDR data as well: Node's `en-IN` joins them with `" at "`, the browser's
 * with `", "`, so the queen schedule heading rendered "Sun, 11 Oct at 12:00 PM"
 * on the server and hydrated as "Sun, 11 Oct, 12:00 pm".
 */
export function formatDayMonthTime(
  value: DateLike,
  { weekday = false }: { weekday?: boolean } = {},
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = zonedDateTimeParts(date);
  if (!parts) return "";

  const dayMonth = `${parts.day} ${shortMonth(parts.monthIndex)}`;
  const prefix = weekday
    ? `${SHORT_WEEKDAYS[new Date(Date.UTC(parts.year, parts.monthIndex, parts.day)).getUTCDay()]}, `
    : "";
  return `${prefix}${dayMonth}, ${clockLabel(parts)}`;
}

const YEAR = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  timeZone: APP_DEFAULT_TIME_ZONE,
});

/** Anything a caller might hold: a `Date`, an ISO string, or epoch millis. */
export type DateLike = Date | string | number;

/**
 * For the labels that read the clock. Pinning `now` is what makes them
 * reproducible in a test; production callers omit it.
 */
export type ClockOptions = {
  now?: number;
};

function toDate(value: DateLike): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * The absolute date a relative label falls back to. Years appear only when the
 * date is not in the current one — compared in the app's zone, so the answer
 * does not flip around New Year depending on where it is computed.
 */
export function formatAbsoluteDay(
  value: DateLike,
  { now = Date.now() }: ClockOptions = {},
): string {
  const date = toDate(value);
  return YEAR.format(date) === YEAR.format(now)
    ? formatDayMonth(date)
    : formatDayMonthYear(date);
}

/**
 * Feed-card style: a bare unit, no suffix — "now", "5m", "3h", "2d", "1w" —
 * falling back to a date after a month.
 */
export function formatRelativeTimeCompact(
  value: DateLike,
  { now = Date.now() }: ClockOptions = {},
): string {
  const date = toDate(value);
  const elapsed = Math.max(0, now - date.getTime());

  if (elapsed < MINUTE) return "now";
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h`;
  if (elapsed < WEEK) return `${Math.floor(elapsed / DAY)}d`;
  if (elapsed < 4 * WEEK) return `${Math.floor(elapsed / WEEK)}w`;
  return formatAbsoluteDay(date, { now });
}

/**
 * Comment style: the same ladder said out loud — "now", "12s ago", "5m ago" —
 * with seconds, because a reply that just landed should say so.
 */
export function formatRelativeTimeSpoken(
  value: DateLike,
  { now = Date.now() }: ClockOptions = {},
): string {
  const date = toDate(value);
  const elapsed = Math.max(0, now - date.getTime());

  if (elapsed < 5 * SECOND) return "now";
  if (elapsed < MINUTE) return `${Math.floor(elapsed / SECOND)}s ago`;
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
  if (elapsed < WEEK) return `${Math.floor(elapsed / DAY)}d ago`;
  if (elapsed < 5 * WEEK) return `${Math.floor(elapsed / WEEK)}w ago`;
  return formatDayMonth(date);
}

/*
 * ---------------------------------------------------------------------------
 * Absolute labels outside the feed
 *
 * The relative ladder above covers posts and comments. Everything else — a
 * booking's start time, a refund SLA, a shipment event, a push-notification
 * body — wants a plain pinned label, in one of a handful of shapes.
 * ---------------------------------------------------------------------------
 */

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
};

const CALENDAR_DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

/**
 * `Intl.DateTimeFormat` construction dominates the cost of formatting, and
 * these run inside calendar grids that format hundreds of values per render.
 * Options come from module-level literals at every call site, so the key space
 * stays small.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(options: Intl.DateTimeFormatOptions) {
  const key = JSON.stringify([
    options.timeZone,
    options.weekday,
    options.year,
    options.month,
    options.day,
    options.hour,
    options.minute,
    options.second,
    options.hour12,
  ]);

  const cached = formatterCache.get(key);
  if (cached) return cached;

  const created = new Intl.DateTimeFormat(RELATIVE_TIME_LOCALE, options);
  formatterCache.set(key, created);
  return created;
}

/**
 * An instant in the app's zone, in whatever shape the caller names. The named
 * helpers below cover the common shapes; reach for this one directly when you
 * need something else (a weekday, say).
 *
 * Returns `""` rather than "Invalid Date" for input that will not parse, so a
 * missing timestamp renders as nothing.
 */
export function formatAbsolute(
  value: DateLike,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return "";

  return getFormatter({ ...options, timeZone: APP_DEFAULT_TIME_ZONE }).format(
    date,
  );
}

/** An instant as day plus wall clock — `"5 Sept 2026, 1:30 am"`. */
export function formatAbsoluteDateTime(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = DATE_TIME_OPTIONS,
): string {
  return formatAbsolute(value, options);
}

/** Just the wall clock an instant shows in the app's zone — `"1:30 am"`. */
export function formatAbsoluteTime(
  value: DateLike,
  options: Intl.DateTimeFormatOptions = TIME_OPTIONS,
): string {
  return formatAbsolute(value, options);
}

/**
 * A `Date` that stands for a square on a calendar, not a point on the timeline.
 *
 * Reads the local Y/M/D — the same parts the grid built the value from and
 * navigates it by — and formats those, so no zone can move the label off the
 * day it labels. `new Date("2026-09-05T00:00:00")` is a *different instant* on
 * a UTC server than in an IST browser, but `getDate()` is 5 in both, which is
 * what makes this hydration-safe where a named zone would not be.
 */
export function formatCalendarDay(
  date: Date,
  options: Intl.DateTimeFormatOptions = CALENDAR_DAY_OPTIONS,
): string {
  if (Number.isNaN(date.getTime())) return "";

  // Midday avoids any risk of a DST or rounding edge pushing the mirror over a
  // boundary; only the date parts are ever read back out.
  const utcMirror = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12),
  );

  return getFormatter({ ...options, timeZone: "UTC" }).format(utcMirror);
}
